/**
 * People's time (T-102): the minutes people spend active in the app, kept one row per person per minute
 * (`human_time`). The browser decides which minutes count (frontend/src/lib/activity-tracker.ts) and sends
 * them to POST /api/activity; the Worker checks them (worker/src/services/human-time/). Shared here: the
 * limits both sides keep, the page areas, and how a page's path names its project and task.
 */

/** A tab stops counting after this long with no typing, clicking, scrolling or touching. */
export const ACTIVITY_IDLE_MS = 2 * 60_000;
/** The most minutes one POST /api/activity takes. */
export const MAX_ACTIVITY_MINUTES = 120;
/** Minutes older than this are refused (and the browser stops holding them). */
export const MAX_ACTIVITY_AGE_MS = 24 * 60 * 60_000;

export const MINUTE_MS = 60_000;

/** Where in the app a minute was spent. */
export const HUMAN_TIME_AREAS = [
  /** A project's Overview, its dashboard (SF-T-43). */
  'overview',
  'board',
  'task',
  'runs',
  'settings',
  'usage',
  'chat',
  'inbox',
  'fleet',
  'projects',
  'agent-config',
  'ai',
  'admin',
  'api-keys',
  'other',
] as const;
export type HumanTimeArea = (typeof HUMAN_TIME_AREAS)[number];

export function isHumanTimeArea(value: unknown): value is HumanTimeArea {
  return typeof value === 'string' && (HUMAN_TIME_AREAS as readonly string[]).includes(value);
}

/**
 * The device a minute was spent on (SF-T-281): "mobile" while the tab showed the phone layout (under
 * 640px wide, Tailwind's `sm`), else "desktop". Minutes from before it was recorded have none (null).
 */
export const HUMAN_TIME_DEVICES = ['mobile', 'desktop'] as const;
export type HumanTimeDevice = (typeof HUMAN_TIME_DEVICES)[number];

export function isHumanTimeDevice(value: unknown): value is HumanTimeDevice {
  return typeof value === 'string' && (HUMAN_TIME_DEVICES as readonly string[]).includes(value);
}

/** One counted minute as the browser sends it. The task's project is the Worker's to find. */
export interface ActivityMinute {
  /** The minute's start, ISO (seconds zero). */
  minute: string;
  /** The tab's project (`/p/<slug>/…`); null on pages outside a project. */
  project_id: string | null;
  /** The task open in the tab (a task panel or page), else null. */
  task_id: string | null;
  area: HumanTimeArea;
  /** Left out by a tab loaded before devices were recorded: kept as null. */
  device?: HumanTimeDevice;
}

/** The start of the minute `ms` falls in. */
export function minuteStart(ms: number): number {
  return Math.floor(ms / MINUTE_MS) * MINUTE_MS;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface PathActivity {
  area: HumanTimeArea;
  /** `/p/<slug>/…`: the project's slug, else null. */
  projectSlug: string | null;
  /** A task open in the page: `/p/<slug>/board/<id>`, `/board/<id>`, `/inbox/<id>`. */
  taskId: string | null;
}

const TOP_AREAS: Record<string, HumanTimeArea> = {
  board: 'board',
  inbox: 'inbox',
  chat: 'chat',
  projects: 'projects',
  runs: 'runs',
  usage: 'usage',
  fleet: 'fleet',
  ai: 'ai',
  'agent-config': 'agent-config',
  admin: 'admin',
  settings: 'api-keys',
};

const PROJECT_AREAS: Record<string, HumanTimeArea> = { overview: 'overview', board: 'board', runs: 'runs', settings: 'settings' };

/** What a page's path says about the minute: its area, its project's slug and the task open in it. */
export function activityFromPath(pathname: string): PathActivity {
  const parts = pathname.split(/[?#]/)[0]!.split('/').filter(Boolean);
  const task = (id: string | undefined) => (id && UUID_RE.test(id) ? id.toLowerCase() : null);
  if (parts[0] === 'p' && parts[1]) {
    // A project's own URL opens its Board (SF-T-189).
    const section = parts[2] ?? 'board';
    const taskId = section === 'board' ? task(parts[3]) : null;
    return { area: taskId ? 'task' : (PROJECT_AREAS[section] ?? 'other'), projectSlug: parts[1], taskId };
  }
  const top = parts[0] ?? 'board';
  const taskId = top === 'board' || top === 'inbox' ? task(parts[1]) : null;
  return { area: taskId ? 'task' : (TOP_AREAS[top] ?? 'other'), projectSlug: null, taskId };
}
